import React, { Fragment, useEffect, useState } from 'react';
import { Container, Row, Col, Card, CardHeader, CardBody, Button, Input, Label, Spinner } from 'reactstrap';
import { Breadcrumbs } from '../../AbstractElements';
import axios from 'axios';
import { toast } from 'react-toastify';
import API_BASE_URL from '../../Config/api';
import authHeader from './authHeader';

const EMPTY = { billing_address: '', shipping_address: '', email: '', tel: '', vat_eori: '' };

const fromProfile = (p) => ({
  billing_address: p.billing_address || '',
  shipping_address: p.shipping_address || '',
  email: p.email || '',
  tel: p.tel || '',
  vat_eori: p.vat_eori || '',
});

const BillingDetails = () => {
  const [form, setForm] = useState(EMPTY);
  // One sign-in can cover several accounts (a chain keeps one per location).
  // Each bills separately — own VAT number, own billing address — so they are
  // edited one at a time rather than merged.
  const [profiles, setProfiles] = useState([]);
  const [accountId, setAccountId] = useState(null);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  useEffect(() => {
    (async () => {
      try {
        const res = await axios.get(`${API_BASE_URL}/api/customer/profile`, authHeader());
        const list = res.data.profiles || (res.data.profile ? [res.data.profile] : []);
        const current = res.data.profile || list[0] || {};
        setProfiles(list);
        setAccountId(current.id ?? null);
        setForm(fromProfile(current));
      } catch (e) {
        console.error('Failed to load profile', e);
        toast.error('Failed to load billing details');
      } finally {
        setLoading(false);
      }
    })();
  }, []);

  const multiAccount = profiles.length > 1;
  const selected = profiles.find((p) => p.id === accountId) || {};
  const storeName = selected.store_name || '';

  // Switching accounts discards unsaved edits to the one being left, so the
  // form always shows what is actually stored for the account on screen.
  const switchAccount = (e) => {
    const id = Number(e.target.value);
    const next = profiles.find((p) => p.id === id);
    if (!next) return;
    setAccountId(id);
    setForm(fromProfile(next));
  };

  const set = (k) => (e) => setForm({ ...form, [k]: e.target.value });

  const save = async (e) => {
    e.preventDefault();
    setSaving(true);
    try {
      const res = await axios.put(
        `${API_BASE_URL}/api/customer/profile`,
        { ...form, customer_id: accountId },
        authHeader()
      );
      const saved = res.data.profile;
      if (saved) setProfiles((prev) => prev.map((p) => (p.id === saved.id ? saved : p)));
      toast.success(`Billing details saved${multiAccount && storeName ? ` for ${storeName}` : ''}`);
    } catch (err) {
      toast.error(err.response?.data?.error || 'Failed to save');
    } finally {
      setSaving(false);
    }
  };

  return (
    <Fragment>
      <Breadcrumbs parent="Dashboard" title="Billing Details" />
      <Container fluid={true}>
        <Row>
          <Col lg="8" xl="6">
            <Card>
              <CardHeader className="pb-0">
                <h5>Billing Details</h5>
                <span>Used on your invoices{storeName ? ` for ${storeName}` : ''}. Its stores inherit this email &amp; phone unless overridden.</span>
              </CardHeader>
              <CardBody>
                {loading ? (
                  <p className="text-muted"><Spinner size="sm" /> Loading...</p>
                ) : (
                  <form onSubmit={save}>
                    {multiAccount && (
                      <>
                        <Label className="form-label">Account</Label>
                        <Input type="select" className="mb-1" value={accountId ?? ''} onChange={switchAccount}>
                          {profiles.map((p) => <option key={p.id} value={p.id}>{p.store_name}</option>)}
                        </Input>
                        <p className="text-muted f-12 mb-3">
                          Each account invoices separately. Switching discards unsaved changes.
                        </p>
                      </>
                    )}
                    <Label className="form-label">Billing Address</Label>
                    <Input type="textarea" rows="3" className="mb-3" value={form.billing_address}
                      placeholder="Street, City, Postcode, Region" onChange={set('billing_address')} />

                    <Label className="form-label">Default Shipping Address</Label>
                    <Input type="textarea" rows="3" className="mb-1" value={form.shipping_address}
                      placeholder="Street, City, Postcode, Region" onChange={set('shipping_address')} />
                    <p className="text-muted f-12 mb-3">Used when a store has no shipping address of its own.</p>

                    <Row>
                      <Col md="6">
                        <Label className="form-label">Email</Label>
                        <Input type="email" className="mb-3" value={form.email}
                          placeholder="name@example.com" onChange={set('email')} />
                      </Col>
                      <Col md="6">
                        <Label className="form-label">Phone</Label>
                        <Input className="mb-3" value={form.tel}
                          placeholder="Phone number" onChange={set('tel')} />
                      </Col>
                    </Row>

                    <Label className="form-label">VAT / EORI</Label>
                    <Input className="mb-2" value={form.vat_eori}
                      placeholder="e.g. EL997744386" onChange={set('vat_eori')} />
                    <p className="text-muted f-12 mb-3">Changing your email here does not change your login email.</p>

                    <Button type="submit" color="primary" disabled={saving}>
                      {saving ? 'Saving...' : 'Save Changes'}
                    </Button>
                  </form>
                )}
              </CardBody>
            </Card>
          </Col>
        </Row>
      </Container>
    </Fragment>
  );
};

export default BillingDetails;
